import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Loader from "../../components/Loader";
import ErrorMessage from "../../components/ErrorMessage";
import { getOrderDetailsById, updateOrderStatus } from "../../services/user";
import {
  FaBoxOpen,
  FaReceipt,
  FaTruck,
  FaCreditCard,
  FaHome,
  FaFileInvoice,
} from "react-icons/fa";

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleAcceptOrder = async () => {
    if (!order || !order._id) return;
    try {
      setAccepting(true);
      setActionError(null);
      const response = await updateOrderStatus(order._id, "Accepted");
      if (response.success) {
        setOrder((prevOrder: any) =>
          prevOrder ? { ...prevOrder, status: "Accepted" } : null
        );
      } else {
        setActionError(response.message || "Failed to accept order.");
      }
    } catch (err: any) {
      console.error("Error accepting order:", err);
      setActionError(
        err.response?.data?.message || err.message || "An error occurred while accepting the order."
      );
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await getOrderDetailsById(id);
        setOrder(response.data);
      } catch (err) {
        setError("Failed to fetch order details. Please try again later.");
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  if (loading) return <Loader />;
  if (error) return <ErrorMessage message={error} />;
  if (!order) return <ErrorMessage message="Order not found" />;

  // Extract data from order object
  const shipping =
    order.logisticPayload?.message?.order?.fulfillments?.[0] || {};
  const breakup = order.orderData?.message?.order?.quote?.breakup || [];
  const totalPrice = order.orderData?.message?.order?.quote?.price?.value || 0;
  const payment = order.orderData?.message?.order?.payment || {};
  const documents = order.orderData?.message?.order?.documents || [];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "accepted":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex justify-between">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
          Order Details
        </h1>
        <div className="w-1/6">
        <button
          onClick={() => window.history.back()}
          type="button"
          className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
        >
          {"Back"}
        </button>
        </div>
       
      </div>

      {/* Order Summary Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FaBoxOpen className="text-blue-500" /> Order Summary
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Order ID: {order.order_id}
            </p>
          </div>
          <div className="mt-2 md:mt-0 flex flex-col items-end gap-2">
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  order.status
                )}`}
              >
                {order.status}
              </span>
              {order.status?.toLowerCase() === "pending" && (
                <button
                  onClick={handleAcceptOrder}
                  disabled={accepting}
                  className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  {accepting ? "Accepting..." : "Accept Order"}
                </button>
              )}
            </div>
            {actionError && (
              <span className="text-xs text-red-600 font-medium">{actionError}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-gray-500 text-sm">Order Date</p>
            <p className="font-medium">
              {new Date(order.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-gray-500 text-sm">Total Amount</p>
            <p className="font-medium">₹{totalPrice}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            {/* <p className="text-gray-500 text-sm">Payment Status</p>
            <p className="font-medium">{order.paymentStatus}</p> */}
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FaBoxOpen className="text-blue-500" /> Products (
          {order.products.length})
        </h2>

        <div className="space-y-4">
          {order.products.map((product: any, index: number) => (
            <div
              key={index}
              className="border-b pb-4 last:border-b-0 last:pb-0"
            >
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <h3 className="font-medium">{product.id.productName}</h3>
                  <p className="text-gray-500 text-sm">
                    Quantity: {product.quantity}
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  <p className="font-medium">₹{product.id.price}</p>
                  {product.id.maxPrice && (
                    <p className="text-gray-500 text-sm line-through">
                      ₹{product.id.maxPrice}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Price Breakdown Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FaReceipt className="text-blue-500" /> Price Breakdown
        </h2>

        <div className="space-y-3">
          {breakup.map((item: any, idx: number) => (
            <div key={idx} className="flex justify-between">
              <span className="text-gray-600">{item.title}</span>
              <span className="font-medium">₹{item.price.value}</span>
            </div>
          ))}
          <div className="border-t pt-3 mt-2 flex justify-between font-semibold">
            <span>Total Amount</span>
            <span>₹{totalPrice}</span>
          </div>
        </div>
      </div>

      {/* Payment Information Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FaCreditCard className="text-blue-500" /> Payment Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-500 text-sm">Payment Method</p>
            <p className="font-medium">{order.paymentType}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Transaction ID</p>
            <p className="font-medium">{payment.params?.transaction_id}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Amount Paid</p>
            <p className="font-medium">₹{payment.params?.amount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Payment Status</p>
            <p className="font-medium">{payment.status}</p>
          </div>
        </div>
      </div>

      {/* Shipping Information Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FaTruck className="text-blue-500" /> Shipping Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-500 text-sm">Delivery Partner</p>
            <p className="font-medium">
              {order.logisticPayload?.message?.order?.provider?.id}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">AWB Number</p>
            <p className="font-medium">{shipping["@ondc/org/awb_no"]}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Status</p>
            <p className="font-medium">{shipping.state?.descriptor?.code}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Shipping Label</p>
            <a
              className="text-blue-600 underline hover:text-blue-800"
              href={shipping.start?.instructions?.images?.[0]}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Shipping Label
            </a>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <h3 className="font-medium mb-2">Pickup Address</h3>
            <p className="text-sm text-gray-600">
              {shipping.start?.location?.address?.building},<br />
              {shipping.start?.location?.address?.city},{" "}
              {shipping.start?.location?.address?.state}
              <br />
              {shipping.start?.location?.address?.area_code}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Contact: {shipping.start?.contact?.phone}
            </p>
          </div>
        </div>
      </div>

      {/* Billing Information Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FaHome className="text-blue-500" /> Billing Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-500 text-sm">Name</p>
            <p className="font-medium">{order.billing.name}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Email</p>
            <p className="font-medium">{order.billing.email}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Phone</p>
            <p className="font-medium">{order.billing.phone}</p>
          </div>
        </div>

        <div className="mt-4 bg-gray-50 p-3 rounded-lg">
          <h3 className="font-medium mb-1">Address</h3>
          <p className="text-sm text-gray-600">
            {order.billing.address.building},<br />
            {order.billing.address.locality &&
              `${order.billing.address.locality},`}
            <br />
            {order.billing.address.city}, {order.billing.address.state}
            <br />
            {order.billing.address.area_code}
          </p>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FaFileInvoice className="text-blue-500" /> Documents
        </h2>

        <div className="space-y-2">
          {documents.map((doc: any, index: number) => (
            <div key={index}>
              <a
                className="text-blue-600 underline hover:text-blue-800"
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {doc.label || `Document ${index + 1}`}
              </a>
            </div>
          ))}
          {order.invoice && (
            <div>
              <a
                className="text-blue-600 underline hover:text-blue-800"
                href={order.invoice}
                target="_blank"
                rel="noopener noreferrer"
              >
                Invoice
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
